/**
 * @fileoverview Vendor/product pill component
 * @module TechStackAnalysis/components/VendorPill
 */

import React from 'react';

/**
 * Format currency value compactly
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
const formatSpend = (value) => {
  if (!value) return '';
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

/**
 * Format contract end date compactly
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date
 */
const formatContractEnd = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/**
 * Determine contract status based on end date
 * @param {string} contractEnd - Contract end date
 * @returns {Object} Status object with class and label
 */
const getContractStatus = (contractEnd) => {
  if (!contractEnd) return { status: 'active', label: '' };

  const endDate = new Date(contractEnd);
  const now = new Date();
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const in90Days = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

  if (endDate < now) return { status: 'expired', label: 'Expired' };
  if (endDate <= in30Days) return { status: 'expiring', label: 'Exp. soon' };
  if (endDate <= in90Days) return { status: 'expiring', label: '' };
  return { status: 'active', label: '' };
};

/**
 * Vendor pill component showing vendor/product as a clickable chip
 * @param {Object} props
 * @param {Object} props.vendor - Vendor data object
 * @param {boolean} props.selected - Whether pill is selected
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onHaptic - Haptic feedback trigger
 */
const VendorPill = ({ vendor, selected, onClick, onHaptic }) => {
  const { status, label } = getContractStatus(vendor.contractEnd);
  const spend = formatSpend(vendor.annualSpend);
  const endDate = formatContractEnd(vendor.contractEnd);

  const handleClick = () => {
    onHaptic?.();
    onClick?.(vendor);
  };

  return (
    <button
      className={`ts-vendor-pill ts-vendor-pill-detailed ${selected ? 'selected' : ''} ${status}`}
      onClick={handleClick}
      title={`${vendor.vendorName} - ${vendor.productName}${spend ? ` | ${spend}` : ''}${endDate ? ` | Ends ${endDate}` : ''}`}
    >
      <div className="ts-vendor-pill-main">
        <span className={`ts-vendor-status ${status}`} />
        <span className="ts-vendor-name">
          {vendor.productName || vendor.vendorName}
        </span>
        {label && <span className="ts-vendor-expiry-badge">{label}</span>}
      </div>
      <div className="ts-vendor-pill-meta">
        {spend && <span className="ts-vendor-spend">{spend}</span>}
        {endDate && <span className="ts-vendor-end-date">→ {endDate}</span>}
      </div>
    </button>
  );
};

export default VendorPill;
